// relationshipValidator.js
export const QUESTION_TYPES = {
  TEXT: "Text",
  CHECKBOX: "CheckBox",
  SELECT: "Select",
  QUANT: "Quant",
  MAP: "Map",
  DATETIME: "DateTime",
};

export const RELATIONSHIP_TYPES = {
  SELF_NODE: "selfNode",
  CREATE_NODES: "createNodes",
  SELF_PROPS: "selfProps",
  ADD_PROPS: "addProps",
  UNKNOWN: "unknown",
};

export const VALIDATION_RULES = {
  [RELATIONSHIP_TYPES.SELF_NODE]: {
    allowedTypes: [
      QUESTION_TYPES.TEXT,
      QUESTION_TYPES.CHECKBOX,
      QUESTION_TYPES.SELECT,
    ],
    encryption: null, // Can have any encryption
    canBeManyToMany: false,
    description: "Creates the main entity (self node) in the network",
  },
  [RELATIONSHIP_TYPES.CREATE_NODES]: {
    allowedTypes: [
      QUESTION_TYPES.TEXT,
      QUESTION_TYPES.CHECKBOX,
      QUESTION_TYPES.SELECT,
    ],
    encryption: null, // Can have any encryption
    canBeManyToMany: true,
    description: "Creates new nodes connected to other nodes",
  },
  [RELATIONSHIP_TYPES.SELF_PROPS]: {
    allowedTypes: [
      QUESTION_TYPES.TEXT,
      QUESTION_TYPES.CHECKBOX,
      QUESTION_TYPES.SELECT,
      QUESTION_TYPES.QUANT,
      QUESTION_TYPES.MAP,
      QUESTION_TYPES.DATETIME,
    ],
    encryption: ["none"], // Must be none
    canBeManyToMany: true,
    description:
      "Adds properties to the self node (any question type can be selfProps)",
  },
  [RELATIONSHIP_TYPES.ADD_PROPS]: {
    allowedTypes: [
      QUESTION_TYPES.TEXT,
      QUESTION_TYPES.CHECKBOX,
      QUESTION_TYPES.SELECT,
      QUESTION_TYPES.QUANT,
      QUESTION_TYPES.MAP,
      QUESTION_TYPES.DATETIME,
    ],
    encryption: ["none"], // Must be none
    canBeManyToMany: true,
    description:
      "Adds properties to existing nodes from other questions (any question type can be nodeProps)",
  },
};

export function validateQuestionTypeForRelationship(
  questionType,
  relationshipType
) {
  const rules = VALIDATION_RULES[relationshipType];
  if (!rules) {
    return {
      isValid: false,
      error: `Unknown relationship type: ${relationshipType}`,
    };
  }

  const isValid = rules.allowedTypes.includes(questionType);

  return {
    isValid,
    error: isValid
      ? null
      : `${questionType} questions cannot be ${relationshipType}. Allowed types: ${rules.allowedTypes.join(
          ", "
        )}`,
    rules,
  };
}

export function getEncryptionForRelationship(relationshipType) {
  const rules = VALIDATION_RULES[relationshipType];
  return rules ? rules.encryption : null; // null means any encryption allowed
}

export function canRelationshipBeManyToMany(relationshipType) {
  const rules = VALIDATION_RULES[relationshipType];
  return rules ? rules.canBeManyToMany : false;
}

export function getRelationshipDescription(relationshipType) {
  const rules = VALIDATION_RULES[relationshipType];
  return rules ? rules.description : "Unknown relationship type";
}

export function getAllowedTypesForRelationship(relationshipType) {
  const rules = VALIDATION_RULES[relationshipType];
  return rules ? rules.allowedTypes : [];
}

export function getValidationSummary(question) {
  const relationshipType = getRelationTypeFromDataset(question.dataset);
  const validation = validateQuestionTypeForRelationship(
    question.kind,
    relationshipType
  );

  const summary = {
    relationshipType,
    questionType: question.kind,
    isValid: validation.isValid,
    error: validation.error,
    rules: validation.rules,
    encryption: getEncryptionForRelationship(relationshipType),
    canBeManyToMany: canRelationshipBeManyToMany(relationshipType),
    description: getRelationshipDescription(relationshipType),
  };

  // Check encryption compliance
  const currentEncryption = question.encrypt || ["none"]; // Default to ['none'] if undefined
  const expectedEncryption = summary.encryption;

  if (expectedEncryption === null) {
    // Any encryption is allowed
    summary.encryptionCompliant = true;
  } else {
    // Must match expected encryption
    summary.encryptionCompliant =
      JSON.stringify(currentEncryption.sort()) ===
      JSON.stringify(expectedEncryption.sort());
  }

  // Check many-to-many compliance
  summary.manyToManyCompliant = !question.manyToMany || summary.canBeManyToMany;

  return summary;
}

function getRelationTypeFromDataset(dataset) {
  if (!dataset || !Array.isArray(dataset)) return RELATIONSHIP_TYPES.UNKNOWN;

  if (dataset.some((item) => item[0] === "setSelf"))
    return RELATIONSHIP_TYPES.SELF_NODE;
  if (dataset.some((item) => item[0] === "nodes"))
    return RELATIONSHIP_TYPES.CREATE_NODES;
  if (dataset.some((item) => item[0] === "nodeProps"))
    return RELATIONSHIP_TYPES.ADD_PROPS;
  if (
    dataset.some(
      (item) =>
        item[0] === "selfProps" && !dataset.some((i) => i[0] === "setSelf")
    )
  ) {
    return RELATIONSHIP_TYPES.SELF_PROPS;
  }

  return RELATIONSHIP_TYPES.UNKNOWN;
}

export function autoFixQuestionCompliance(question) {
  const validation = getValidationSummary(question);
  const fixes = [];

  if (!validation.isValid) {
    fixes.push({
      type: "error",
      message: validation.error,
      canAutoFix: false,
    });
  }

  if (!validation.encryptionCompliant && validation.encryption !== null) {
    fixes.push({
      type: "encryption",
      message: `Auto-fixing encryption: ${JSON.stringify(
        question.encrypt
      )} → ${JSON.stringify(validation.encryption)}`,
      canAutoFix: true,
      fix: () => ({ ...question, encrypt: validation.encryption }),
    });
  }

  if (!validation.manyToManyCompliant) {
    fixes.push({
      type: "manyToMany",
      message: `Removing many-to-many (not supported for ${validation.relationshipType})`,
      canAutoFix: true,
      fix: () => {
        const fixed = { ...question };
        delete fixed.manyToMany;
        delete fixed.fieldLabels;
        delete fixed.startingFields;
        return fixed;
      },
    });
  }

  return fixes;
}
